import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { BuildingIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { SidebarMenuButton } from "@/components/ui/sidebar"
import { useIsMobile } from "@/hooks/use-mobile"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"

export interface Broker {
  id: string
  name: string
}

interface BrokerSelectorBaseProps {
  brokers: Broker[]
  onOpenChange?: (open: boolean) => void
  className?: string
}

interface SidebarVariantProps extends BrokerSelectorBaseProps {
  variant?: "sidebar"
  selectedBroker: Broker
  onBrokerSelect: (broker: Broker) => void
  onSidebarOpen?: () => void
}

interface FormVariantProps extends BrokerSelectorBaseProps {
  variant: "form"
  selectedBrokerId?: string
  onBrokerSelect: (brokerId: string) => void
  disabled?: boolean
}

type BrokerSelectorProps = SidebarVariantProps | FormVariantProps

export const BrokerSelector = (props: BrokerSelectorProps) => {
  const { brokers, onOpenChange, className } = props
  const { t } = useTranslation(); // namespace: brokerDelivery
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const isMobile = useIsMobile()

  const filteredBrokers = React.useMemo(() => {
    if (!search.trim()) return brokers
    const searchLower = search.toLowerCase()
    return brokers.filter((broker) =>
      broker.name.toLowerCase().includes(searchLower)
    )
  }, [brokers, search])

  const handleSearchChange = (value: string) => {
    const previousSearch = search
    setSearch(value)

    if (previousSearch && !value.trim()) {
      setTimeout(() => {
        const listElement = document.querySelector('[cmdk-list]')
        if (listElement) {
          listElement.scrollTop = 0
        }
      }, 0)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    onOpenChange?.(newOpen)
    if (!newOpen) setSearch("")
  }

  const handleBrokerSelect = (broker: Broker) => {
    if (props.variant === "form") {
      props.onBrokerSelect(broker.id)
    } else {
      props.onBrokerSelect(broker)
    }
    setOpen(false)
    setSearch("")
  }

  const getSelectedBrokerId = (): string | undefined => {
    if (props.variant === "form") {
      return props.selectedBrokerId
    }
    return props.selectedBroker?.id
  }

  const getSelectedBrokerName = (): string => {
    if (props.variant === "form") {
      const broker = brokers.find(b => b.id === props.selectedBrokerId)
      return broker?.name || t("selector.selectBroker")
    }
    return props.selectedBroker?.name || t("selector.selectBroker")
  }

  const selectedBrokerId = getSelectedBrokerId()

  if (props.variant === "form") {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={props.disabled}
            className={cn("w-full justify-between font-normal", className)}
          >
            {getSelectedBrokerName()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t("selector.searchBroker")}
              value={search}
              onValueChange={handleSearchChange}
            />
            <CommandList>
              <CommandEmpty>{t("selector.noBrokerFound")}</CommandEmpty>
              <CommandGroup>
                {filteredBrokers.map((broker) => (
                  <CommandItem
                    key={broker.id}
                    value={broker.id}
                    onSelect={() => handleBrokerSelect(broker)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedBrokerId === broker.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {broker.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }

  const handleSidebarAndPopover = () => {
    if ("onSidebarOpen" in props && props.onSidebarOpen) {
      props.onSidebarOpen()
    }
    setOpen(true)
  }

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className={cn(
              "h-fit data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
              className
            )}
          >
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary/70 text-sidebar-primary-foreground">
              <BuildingIcon className="size-4" />
            </div>
            <div className="flex flex-col gap-0.5 leading-none">
              <span className="font-semibold">
                {getSelectedBrokerName()}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </DrawerTrigger>

        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {t("selector.selectBroker")}
            </DrawerTitle>
            <DrawerDescription>
              {t("selector.searchBroker")}
            </DrawerDescription>
          </DrawerHeader>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t("selector.searchBroker")}
              className="h-9"
              value={search}
              onValueChange={handleSearchChange}
            />
            <CommandList>
              <CommandEmpty>
                {t("selector.noBrokerFound")}
              </CommandEmpty>
              <CommandGroup>
                {filteredBrokers.map((broker) => (
                  <CommandItem
                    key={broker.id}
                    value={broker.id}
                    onSelect={() => handleBrokerSelect(broker)}
                  >
                    {broker.name}
                    <Check
                      className={cn(
                        "ml-auto",
                        selectedBrokerId === broker.id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className={cn(
            "h-fit data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
            className
          )}
          onClick={handleSidebarAndPopover}
        >
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary/70 text-sidebar-primary-foreground">
            <BuildingIcon className="size-4" />
          </div>
          <div className="flex flex-col gap-0.5 leading-none">
            <span className="font-semibold">
              {getSelectedBrokerName()}
            </span>
          </div>
          <ChevronsUpDown className="ml-auto" />
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t("selector.searchBroker")}
            className="h-9"
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandList>
            <CommandEmpty>
              {t("selector.noBrokerFound")}
            </CommandEmpty>
            <CommandGroup>
              {filteredBrokers.map((broker) => (
                <CommandItem
                  key={broker.id}
                  value={broker.id}
                  onSelect={() => handleBrokerSelect(broker)}
                >
                  {broker.name}
                  <Check
                    className={cn(
                      "ml-auto",
                      selectedBrokerId === broker.id
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
